// Schreiben-Assistent: Voreinstellung der Zustellwege je Empfänger.
// #587: hinterlegte Wege aus den Stammdaten (E-Mail / Post / Portal, mehrere möglich); ohne Auswahl gilt #586:
// E-Mail, wenn vorhanden, sonst Post. Vorlage „immer zusätzlich per Post“ fügt Post hinzu. Unmögliches fällt weg.
// Etappe 3: Schriftform → Druckstapel (Original mit eigenhändiger Unterschrift), E-Mail/Portal nur vorab; Sperrvermerk.
import { anschriftZuEmpfaenger } from '../epost/anschrift';
import { pruefeEmpfaenger } from '../epost/epost';
import { ZUSTELLWEGE, type SchreibenEmpfaenger, type Zustellweg } from './typen';

type Kontakt = Pick<SchreibenEmpfaenger, 'email' | 'zustellwege' | 'anschrift' | 'portal' | 'objektId'> & { art?: SchreibenEmpfaenger['art'] };

/** Welcher Weg für diesen Empfänger überhaupt geht. Portal braucht eine Objektakte und – #607 – einen Mieter bzw.
 *  Eigentümer: die Freigabe gilt auch ohne Portal-Login und wird sichtbar, sobald der Zugang freigeschaltet ist.
 *  Ohne bekannte Art (ältere Aufrufer) wie bisher nur mit Portalzugang.
 *  Druck geht immer (ohne Anschrift: selbst zustellen, z. B. Einwurf durch den Hausmeister). */
export function moeglich(e: Kontakt): Record<Zustellweg, boolean> {
  const a = anschriftZuEmpfaenger(e.anschrift);
  const portalArt = e.art === 'mieter' || e.art === 'eigentuemer';
  return { email: !!e.email?.trim(), post: !!a && pruefeEmpfaenger(a).length === 0, portal: (e.art ? portalArt : e.portal) && !!e.objektId, druck: true };
}

/** #607: Portal-Freigabe ohne bestehenden Login – Hinweis für die Zeile im Versand-Schritt. */
export function portalOhneLogin(e: Kontakt): boolean {
  return moeglich(e).portal && !e.portal;
}

export const SCHRIFTFORM_SPERRE = 'Schriftform: Original per Druck oder Post nötig – E-Mail und Portal nur zusätzlich als Vorab-Info.';

/** Grund, warum dieser Empfänger nicht gesendet wird (Sperrvermerk, Schriftform ohne Original) – sonst null. */
export function versandSperre(e: Pick<SchreibenEmpfaenger, 'sperrvermerk'>, wege: Zustellweg[], schriftform: boolean): string | null {
  const vermerk = e.sperrvermerk?.trim();
  if (vermerk) return `Sperrvermerk: ${vermerk}`;
  if (schriftform && wege.length && !wege.includes('druck') && !wege.includes('post')) return SCHRIFTFORM_SPERRE;
  return null;
}

const GRUND: Record<Zustellweg, string> = {
  email: 'Keine E-Mail-Adresse – E-Mail nicht möglich.',
  post: 'Keine vollständige Anschrift – Post nicht möglich.',
  portal: 'Kein Portalzugang – Portal nicht möglich.',
  druck: '',
};

export function standardWege(e: Kontakt, immerPost: boolean, schriftform = false): { wege: Zustellweg[]; warnung: string | null } {
  // Vorauswahl nur mit echtem Portalzugang – ohne Login wählt man das Portal bewusst selbst (#607)
  const m = { ...moeglich(e), portal: e.portal && !!e.objektId };
  // Schriftform: Original aus dem Druckstapel, eigenhändig unterschrieben; E-Mail/Portal kann man als Vorab-Info dazunehmen
  if (schriftform) return { wege: ['druck'], warnung: m.post ? null : 'Keine vollständige Anschrift – Brief selbst zustellen (z. B. Einwurf durch den Hausmeister).' };
  if (!m.email && !m.post && !m.portal) return { wege: [], warnung: 'Weder E-Mail noch Anschrift – nicht versendbar.' };
  const automatisch = !e.zustellwege.length;
  const ersatz: Zustellweg = m.email ? 'email' : m.post ? 'post' : 'portal';
  const wunsch = new Set<Zustellweg>(automatisch ? [ersatz] : e.zustellwege);
  if (immerPost) wunsch.add('post');
  const wege = ZUSTELLWEGE.filter((w) => wunsch.has(w) && m[w]);
  // Hinweis: gewünschter, aber unmöglicher Weg – automatisch fehlt die Anschrift (#586: „E-Mail mit Warnung“)
  const fehlt = ZUSTELLWEGE.find((w) => wunsch.has(w) && !m[w]) ?? (automatisch && !m.post ? 'post' : undefined);
  return { wege: wege.length ? wege : [ersatz], warnung: fehlt ? GRUND[fehlt] : null };
}
